/** @format */

import { IconButton } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import ContentEditorBlockBodyCasaNumber from './blockBodies/ContentEditorBlockBodyCasaNumber';
import ContentEditorBlockBodyContent from './blockBodies/ContentEditorBlockBodyContent';
import ContentEditorBlockBodyDocument from './blockBodies/ContentEditorBlockBodyDocument';
import ContentEditorBlockBodyImage from './blockBodies/ContentEditorBlockBodyImage';
import ContentEditorBlockBodyImageText from './blockBodies/ContentEditorBlockBodyImageText';
import ContentEditorBlockBodyScalar from './blockBodies/ContentEditorBlockBodyScalar';
import ContentEditorBlockBodyVideo from './blockBodies/ContentEditorBlockBodyVideo';
import { CloseIcon, DragIcon } from 'src/components/icons';
import { EDITOR_BLOCK_ENABLE_MOVE_BUTTONS } from 'src/constants';
import type { ContentEditorPart, ContentPartFileHandlerFn, PatchContentPartFn } from 'src/types';

interface ContentEditorBlockCardProps {
  part: ContentEditorPart;
  index: number;
  total: number;
  onPatch: PatchContentPartFn;
  onRemove: (partId: string | number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onFile?: ContentPartFileHandlerFn;
  error?: string;
  fieldErrors?: { headline?: string; paragraph?: string };
}

export default function ContentEditorBlockCard({
  part,
  index,
  total,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
  onFile = undefined,
  error = '',
  fieldErrors
}: ContentEditorBlockCardProps) {
  const { t } = useTranslation();
  const label = t(`contentEditor.blockTypes.${part.type}`, { defaultValue: part.type });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: part.id
  });
  const dragTransform = transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined;
  const dragStyle = {
    transform: dragTransform,
    transition
  };

  const handleFile = (file: File | File[] | null) => {
    if (typeof onFile === 'function') {
      onFile(part, file);
    }
  };

  const renderBody = () => {
    switch (part.type) {
      case 'content':
        return <ContentEditorBlockBodyContent part={part} onPatch={onPatch} />;
      case 'salesInformationNo':
        return (
          <ContentEditorBlockBodyScalar
            part={part}
            fieldKey="salesInformationNo"
            fieldLabel={label}
            onPatch={onPatch}
          />
        );
      case 'serviceInformationNo':
        return <ContentEditorBlockBodyScalar part={part} fieldKey="siNumber" fieldLabel={label} onPatch={onPatch} />;
      case 'casaNumber':
        return <ContentEditorBlockBodyCasaNumber part={part} onPatch={onPatch} />;
      case 'image':
        return <ContentEditorBlockBodyImage part={part} onFile={handleFile} />;
      case 'imageText':
        return (
          <ContentEditorBlockBodyImageText
            part={part}
            onPatch={onPatch}
            onFile={handleFile}
            headlineError={fieldErrors?.headline}
            paragraphError={fieldErrors?.paragraph}
          />
        );
      case 'video':
        return <ContentEditorBlockBodyVideo part={part} onFile={handleFile} />;
      case 'documents':
        return <ContentEditorBlockBodyDocument part={part} onPatch={onPatch} onFile={handleFile} />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={setNodeRef}
      className={`content-editor__block-card${isDragging ? ' content-editor__block-card--dragging' : ''}`}
      style={dragStyle}
      data-part-type={part.type}>
      <div className="content-editor__block-card-toolbar">
        <button
          type="button"
          className="content-editor__block-handle"
          aria-label={t('contentEditor.ariaReorderBlock')}
          {...attributes}
          {...listeners}>
          <DragIcon width={18} height={18} />
        </button>
        <span className="content-editor__block-type">{label}</span>
        <div className="content-editor__block-toolbar-actions">
          {EDITOR_BLOCK_ENABLE_MOVE_BUTTONS && (
            <>
              <IconButton
                size="small"
                aria-label={t('contentEditor.ariaMoveBlockUp')}
                disabled={index < 1}
                onClick={() => onMoveUp(index)}>
                <KeyboardArrowUp fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={t('contentEditor.ariaMoveBlockDown')}
                disabled={index >= total - 1}
                onClick={() => onMoveDown(index)}>
                <KeyboardArrowDown fontSize="small" />
              </IconButton>{' '}
            </>
          )}
          <IconButton size="small" aria-label={t('contentEditor.ariaRemoveBlock')} onClick={() => onRemove(part.id)}>
            <CloseIcon width={14} height={14} />
          </IconButton>
        </div>
      </div>
      <div className="content-editor__block-card-body">{renderBody()}</div>
      {error ? (
        <p className="content-editor__field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
